import { safeValidateAddress } from '@mls-wallet/shared-types';
import type { AddressBookEditInstruction } from '../../types/address-book';

type AddressBookUpsertInstruction = Extract<
  AddressBookEditInstruction,
  { action: 'upsert' }
>;

export type ParsedAddressBookCsv = {
  instructions: AddressBookUpsertInstruction[];
  rowCount: number;
};

/**
 * Parse the address-book import CSV.
 *
 * Contract:
 * - header must be exactly `name,address` (case-insensitive, whitespace-tolerant)
 * - every non-empty row must contain exactly two columns
 * - `name` is trimmed and required
 * - `address` is validated and normalized to checksum form
 *
 * The importer is intentionally strict because this dialog is the bulk-write
 * path for address book labels. Failing early with a specific row number is
 * safer than guessing at malformed CSV input.
 */
export function parseAddressBookCsv(csvText: string): ParsedAddressBookCsv {
  const rows = parseCsvRows(csvText.replace(/^\uFEFF/, '')).filter((row) =>
    row.some((cell) => cell.trim().length > 0)
  );

  if (rows.length === 0) {
    throw new Error('CSV is empty.');
  }

  const headerRow = rows[0];
  const dataRows = rows.slice(1);

  if (!headerRow) {
    throw new Error('CSV is empty.');
  }
  const normalizedHeader = headerRow.map((cell) => cell.trim().toLowerCase());

  if (
    normalizedHeader.length !== 2 ||
    normalizedHeader[0] !== 'name' ||
    normalizedHeader[1] !== 'address'
  ) {
    throw new Error('CSV header must be exactly: name,address');
  }

  if (dataRows.length === 0) {
    throw new Error('CSV must include at least one address row.');
  }

  const instructions = dataRows.map((row, index) => {
    const displayRowNumber = index + 2;

    if (row.length !== 2) {
      throw new Error(
        `Row ${displayRowNumber} must have exactly 2 columns: name,address`
      );
    }

    const name = row[0]?.trim() ?? '';
    const rawAddress = row[1]?.trim() ?? '';

    if (name.length === 0) {
      throw new Error(`Row ${displayRowNumber} is missing a name.`);
    }

    const addressValidation = safeValidateAddress(rawAddress);

    if (!addressValidation.isValid) {
      throw new Error(`Row ${displayRowNumber} has an invalid address.`);
    }

    return {
      action: 'upsert',
      name,
      address: addressValidation.checksumAddress,
    } as const;
  });

  return {
    instructions,
    rowCount: instructions.length,
  };
}

/**
 * Minimal CSV parser with quoted-field support.
 *
 * This handles:
 * - commas inside quoted fields
 * - escaped quotes via `""`
 * - CRLF or LF line endings
 *
 * It intentionally does not try to be a full spreadsheet engine; it only
 * implements the pieces needed for a reliable two-column import file.
 */
function parseCsvRows(csvText: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;

  const pushCell = () => {
    currentRow.push(currentCell);
    currentCell = '';
  };

  const pushRow = () => {
    pushCell();
    rows.push(currentRow);
    currentRow = [];
  };

  for (let index = 0; index < csvText.length; index += 1) {
    const character = csvText[index];
    const nextCharacter = csvText[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentCell += '"';
        index += 1;
        continue;
      }

      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && character === ',') {
      pushCell();
      continue;
    }

    if (!inQuotes && (character === '\n' || character === '\r')) {
      if (character === '\r' && nextCharacter === '\n') {
        index += 1;
      }

      pushRow();
      continue;
    }

    currentCell += character;
  }

  if (inQuotes) {
    throw new Error('CSV contains an unterminated quoted field.');
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    pushRow();
  }

  return rows;
}
